'use client';

import { useCallback } from 'react';
import type { DocumentMarker } from '@frameio/graphql-client/generated';
import { datadogLogger } from '@frameio/telemetry/datadog/logger';
import { useGetDocumentMarkersLazyQuery } from '../../../shared/gql';
import {
  type ExportRange,
  isFiniteNumber,
  useExportMarkersAsComments,
} from '../../../shared/hooks/useCreateCommentsFromMarkers';

/**
 * Builds an export-range window from the AE Render Queue work area (both
 * values in microseconds, as forwarded by the host). Returns `null` when the
 * host doesn't expose the work area (older plugin) or the values aren't
 * usable — the shared hook treats `null` as "full composition".
 *
 * Negative `workAreaStart` is rejected: the schema field is just a `Float`
 * and a negative start would shift every comment by a positive offset
 * (range subtracts a negative), silently corrupting timestamps.
 */
const exportRange = (
  workAreaStart: number | null,
  workAreaDuration: number | null
): ExportRange | null => {
  if (
    !isFiniteNumber(workAreaStart) ||
    workAreaStart < 0 ||
    !isFiniteNumber(workAreaDuration) ||
    workAreaDuration <= 0
  ) {
    return null;
  }
  return {
    startMicroseconds: workAreaStart,
    endMicroseconds: workAreaStart + workAreaDuration,
  };
};

/**
 * After Effects: after upload, turn timeline markers on a composition into
 * Frame.io comments when `exportMarkersAsCommentsEnabled` is true.
 *
 * AE host markers already arrive with `start` in **microseconds** (see the
 * `secondsToMicroseconds` call in the dva-js AE resolver), so they pass
 * straight through to {@link useExportMarkersAsComments} without per-marker
 * normalization. The shared hook's `isFiniteNumber` check + warn surfaces any
 * upstream regressions.
 *
 * When the rendered file covers a sub-range of the composition (Render Queue
 * "Time Span" set to Work Area Only or a custom span), the caller passes
 * `workAreaStart` / `workAreaDuration` (both in microseconds). Markers outside
 * the rendered range are skipped, and surviving markers are timestamp-shifted
 * so they line up with the rendered file in Frame.io. This mirrors the
 * Premiere flow's in/out range handling.
 */
export const useCreateCommentsFromMarkers = () => {
  const [fetchDocumentMarkers] = useGetDocumentMarkersLazyQuery({
    context: { resolveWithUXP: true },
    fetchPolicy: 'network-only',
  });
  const exportMarkersAsComments = useExportMarkersAsComments();

  /** Fetches host markers for `documentId`; returns `undefined` if id is
   * missing or the request fails. */
  const fetchMarkersForDocument = useCallback(
    async ({
      documentId,
    }: {
      documentId: string | null | undefined;
    }): Promise<DocumentMarker[] | undefined> => {
      if (!documentId) {
        return undefined;
      }

      try {
        const { data } = await fetchDocumentMarkers({
          variables: { documentId },
        });
        return data?.documentMarkers;
      } catch (error: unknown) {
        datadogLogger.error(
          'integrations-ae-marker-comments-fetch-markers-failed',
          {
            documentId,
            error: error instanceof Error ? error.message : String(error),
          }
        );
        return undefined;
      }
    },
    [fetchDocumentMarkers]
  );

  const createCommentsFromMarkers = useCallback(
    async ({
      assetId,
      documentId,
      workAreaDuration = null,
      workAreaStart = null,
      framesPerSecond = null,
    }: {
      assetId: string;
      documentId?: string | null;
      workAreaDuration?: number | null;
      workAreaStart?: number | null;
      framesPerSecond?: number | null;
    }) => {
      const markers = await fetchMarkersForDocument({ documentId });
      if (!markers?.length) {
        return;
      }

      await exportMarkersAsComments({
        assetId,
        documentId,
        markers,
        range: exportRange(workAreaStart, workAreaDuration),
        framesPerSecond,
      });
    },
    [exportMarkersAsComments, fetchMarkersForDocument]
  );

  return { createCommentsFromMarkers };
};

export const testExports = {
  exportRange,
};
